import { useSyncExternalStore } from 'react'
import toast from 'react-hot-toast'
import type { BatchImport } from '@/api'

let pendingFolders: BatchImport[] = []
let folderCreationToastId: string | null = null
const listeners = new Set<() => void>()

const emit = () => {
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = () => pendingFolders

export const getPendingCreatedFolders = () => pendingFolders

export const addPendingCreatedFolder = (folder: BatchImport) => {
  if (!folder.fbid) return
  if (pendingFolders.some((item) => item.fbid === folder.fbid)) return
  pendingFolders = [folder, ...pendingFolders]
  emit()
}

export const reconcilePendingCreatedFolders = (
  serverRows: BatchImport[] | undefined
) => {
  if (!serverRows?.length || pendingFolders.length === 0) return
  const serverIds = new Set(
    serverRows.map((row) => row.fbid).filter((id): id is string => Boolean(id))
  )
  const next = pendingFolders.filter(
    (folder) => !folder.fbid || !serverIds.has(folder.fbid)
  )
  if (next.length === pendingFolders.length) return
  pendingFolders = next
  dismissFolderCreationToast()
  emit()
}

export const setFolderCreationToastId = (id: string) => {
  folderCreationToastId = id
}

export const dismissFolderCreationToast = () => {
  if (!folderCreationToastId) return
  toast.dismiss(folderCreationToastId)
  folderCreationToastId = null
}

export const mergePendingCreatedFolders = (
  serverRows: BatchImport[] | undefined,
  pending: BatchImport[]
): BatchImport[] => {
  const rows = serverRows ?? []
  if (pending.length === 0) return rows
  const serverIds = new Set(
    rows.map((row) => row.fbid).filter((id): id is string => Boolean(id))
  )
  const extra = pending.filter(
    (folder) => folder.fbid && !serverIds.has(folder.fbid)
  )
  return extra.length ? [...extra, ...rows] : rows
}

export const clearPendingCreatedFolders = () => {
  dismissFolderCreationToast()
  if (pendingFolders.length === 0) return
  pendingFolders = []
  emit()
}

export const usePendingCreatedFolders = () =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
